import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { MapPin, Clock, Navigation } from "lucide-react";
import { API } from "@/lib/api";

export default function LocationSection() {
  const [schedule, setSchedule] = useState([]);
  const todayIndex = useMemo(() => (new Date().getDay() + 6) % 7, []);
  const [selected, setSelected] = useState(todayIndex);

  useEffect(() => {
    axios
      .get(`${API}/schedule`)
      .then((res) => setSchedule(res.data))
      .catch(() => setSchedule([]));
  }, []);

  const today = schedule.find((e) => e.day_index === todayIndex);
  const active = schedule.find((e) => e.day_index === selected);
  const mapEntry = active && !active.closed && active.map_query ? active : null;

  return (
    <section
      id="find-us"
      data-testid="location-section"
      className="relative py-24 sm:py-36 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mb-14 sm:mb-20"
        >
          <p className="text-disco-yellow text-xs font-semibold tracking-[0.35em] uppercase mb-4">
            Location Tracker
          </p>
          <h2 className="font-display uppercase leading-[0.9] tracking-tight text-5xl sm:text-7xl lg:text-8xl text-[#F8F8F8]">
            Find <span className="neon-teal">the cart</span>
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-8 lg:gap-12">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-2"
          >
            <div
              data-testid="today-card"
              className="border border-disco-teal/40 bg-disco-teal/5 p-7 sm:p-9 relative overflow-hidden"
            >
              <span className="absolute top-0 left-0 h-1 w-full bg-disco-teal animate-pulse-glow" />
              <p className="text-[10px] font-semibold tracking-[0.3em] uppercase text-disco-teal mb-4">
                Today's Pitch
              </p>
              {today ? (
                <>
                  <h3 className="font-display uppercase text-3xl sm:text-4xl text-[#F8F8F8] leading-none">
                    {today.location}
                  </h3>
                  <div className="mt-5 flex items-center gap-2 text-sm text-zinc-300">
                    <Clock size={15} className="text-disco-yellow" />
                    <span data-testid="today-hours">{today.hours}</span>
                  </div>
                  {today.note && (
                    <p className="mt-4 text-sm text-zinc-400 font-light">{today.note}</p>
                  )}
                  {today.closed && (
                    <span
                      data-testid="today-closed-badge"
                      className="mt-5 inline-block rounded-full border border-disco-lavender/50 text-disco-lavender text-xs font-semibold px-4 py-1.5"
                    >
                      No public pitch today
                    </span>
                  )}
                </>
              ) : (
                <p className="text-zinc-500 text-sm">Loading today's spot...</p>
              )}
            </div>

            <div
              data-testid="map-frame"
              className="mt-6 border border-white/10 h-64 sm:h-72 overflow-hidden bg-disco-surface"
            >
              {mapEntry ? (
                <iframe
                  title={`Map to ${mapEntry.location}`}
                  data-testid="location-map-iframe"
                  src={`https://maps.google.com/maps?q=${encodeURIComponent(mapEntry.map_query)}&output=embed`}
                  className="w-full h-full border-0"
                  style={{ filter: "grayscale(1) invert(0.92) contrast(0.9) hue-rotate(180deg)" }}
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center gap-3 text-zinc-500">
                  <Navigation size={22} className="text-disco-teal/60" />
                  <p className="text-xs tracking-wide uppercase">
                    {active?.closed ? "No public location - pick an open day" : "Select a day to preview the map"}
                  </p>
                </div>
              )}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-3"
          >
            <div data-testid="schedule-list" className="border border-white/10 divide-y divide-white/5 bg-disco-surface/50">
              {schedule.map((entry) => {
                const isToday = entry.day_index === todayIndex;
                const isSelected = entry.day_index === selected;
                return (
                  <button
                    key={entry.day_index}
                    data-testid={`schedule-row-${entry.day_index}`}
                    onClick={() => setSelected(entry.day_index)}
                    className={`w-full text-left grid grid-cols-[92px_1fr_auto] sm:grid-cols-[120px_1fr_auto] items-center gap-3 px-4 sm:px-6 py-4 transition-colors duration-300 ${
                      isSelected ? "bg-disco-teal/10 border-l-2 border-disco-teal" : "border-l-2 border-transparent hover:bg-white/5"
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <span className={`font-display uppercase tracking-wider text-lg ${isToday ? "text-disco-teal" : "text-zinc-400"}`}>
                        {entry.day.slice(0, 3)}
                      </span>
                      {isToday && (
                        <span
                          data-testid="schedule-today-badge"
                          className="rounded-full bg-disco-teal text-disco-void text-[9px] font-bold px-2 py-0.5 uppercase"
                        >
                          Today
                        </span>
                      )}
                    </span>
                    <span className="flex items-center gap-2 min-w-0">
                      <MapPin size={13} className={entry.closed ? "text-zinc-600" : "text-disco-yellow"} />
                      <span className={`text-sm truncate ${entry.closed ? "text-zinc-500" : "text-zinc-200"}`}>
                        {entry.location}
                      </span>
                    </span>
                    <span className={`text-xs sm:text-sm font-mono ${entry.closed ? "text-zinc-600" : "text-disco-teal"}`}>
                      {entry.hours}
                    </span>
                  </button>
                );
              })}
            </div>
            <p className="mt-4 text-xs text-zinc-500">
              Tap a day to preview its pitch on the map. Locations are updated weekly by the cart crew.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
